import * as pdfjs from 'pdfjs-dist'
import trabajador from 'pdfjs-dist/build/pdf.worker.min.mjs?url'
import type { ProyectoPlan, PlanModulo } from './tipos'

pdfjs.GlobalWorkerOptions.workerSrc = trabajador

/**
 * Palabras clave que indican una actividad real ejecutable de obra o montaje
 */
const PALABRAS_ACCION = /(INSTALACI|SUMINISTRO|EXCAVACI|CANALIZACI|FUNDICI|CERTIFICACI|RETIRO|REUBICACI|ADECUACI|MONTAJE|MAMPOSTERIA|MARQUILLADO|TRAMITE|TABLERO|CONEXOS)/i

/**
 * Lee un archivo (PDF o TXT) y extrae su texto completo en memoria
 */
export async function extraerTextoDeArchivo(archivo: File): Promise<string> {
  const extension = archivo.name.split('.').pop()?.toLowerCase()

  if (extension === 'pdf') {
    const arrayBuffer = await archivo.arrayBuffer()
    const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise
    let textoCompleto = ''
    for (let i = 1; i <= pdf.numPages; i++) {
      const pagina = await pdf.getPage(i)
      const contenido = await pagina.getTextContent()
      const cadenas = contenido.items.map((it: any) => it.str)
      textoCompleto += cadenas.join('\n') + '\n'
    }
    return textoCompleto
  }

  // Archivos .txt o texto plano
  return await archivo.text()
}

/**
 * Analiza el texto del presupuesto y construye un borrador de ProyectoPlan
 * dividiendo limpiamente en sus etapas correspondientes y filtrando actividades de montaje.
 */
export function procesarTextoPresupuesto(
  texto: string,
  nombreArchivo: string
): ProyectoPlan {
  const lineas = texto
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)

  const id = `proy-import-${Date.now()}`

  // 1. Detectar Nombre del Proyecto
  let nombre = nombreArchivo
    .replace(/\.[^/.]+$/, '')
    .replace(/_texto$/i, '')
    .replace(/[_-]/g, ' ')
    .trim()

  for (const l of lineas.slice(0, 40)) {
    if (
      /(PROPUESTA COMERCIAL|PROPUESTA T[EÉ]CNICA|OBRA EL[EÉ]CTRICA|RETIRO Y REUBICACI[OÓ]N|PLAZA DE MERCADO|ESTADIO|CANCHA)/i.test(
        l
      ) &&
      l.length > 10 &&
      l.length < 130 &&
      !l.includes('Tengo poder legal')
    ) {
      nombre = l.trim()
      break
    }
  }

  // 2. Detectar Solicitante / Cliente
  let cliente: string | null = null
  for (let i = 0; i < Math.min(lineas.length, 50); i++) {
    const l = lineas[i]
    if (/(Solicitante|Cliente|Estimado(a)|Consorcio):/i.test(l)) {
      cliente = l.replace(/(Solicitante|Cliente|Estimado(a)|Consorcio):/i, '').trim()
      if (!cliente && lineas[i + 1]) cliente = lineas[i + 1].trim()
      break
    }
  }

  // 3. Detectar Ubicación / Sitio
  let ubicacion: string | null = null
  for (let i = 0; i < Math.min(lineas.length, 50); i++) {
    const l = lineas[i]
    if (/(Sitio|Ciudad|Direcci[oó]n|Ubicaci[oó]n):/i.test(l)) {
      ubicacion = l.replace(/(Sitio|Ciudad|Direcci[oó]n|Ubicaci[oó]n):/i, '').trim()
      if (!ubicacion && lineas[i + 1]) ubicacion = lineas[i + 1].trim()
      break
    }
  }

  // 4. Mapeo de nombres de capítulos detectados
  const nombresCapitulos: Record<number, string> = {}
  for (const l of lineas) {
    const capMatch = l.match(/^(\d{1,2})\.\s+([A-ZÁÉÍÓÚÑ\s\(\)\/\-]{4,70})$/)
    if (
      capMatch &&
      !l.includes('Tengo poder') &&
      !l.includes('NUESTROS SERVICIOS') &&
      !l.includes('CONDICIONES COMERCIALES') &&
      !l.includes('PRESENTACIÓN')
    ) {
      const num = parseInt(capMatch[1], 10)
      if (num >= 1 && num <= 30) {
        nombresCapitulos[num] = capMatch[2].trim()
      }
    }
  }

  // 5. Agrupar módulos ejecutables por etapa
  const etapasMap: Record<
    number,
    { orden: number; nombre: string; modulos: PlanModulo[] }
  > = {}

  let etapaActualNum = 1

  for (let i = 0; i < lineas.length; i++) {
    const l = lineas[i]

    // Comprobar si esta línea o la anterior tiene código presupuestal (ej. 1.1.1, 1.10, NP)
    const prevLine = i > 0 ? lineas[i - 1] : ''
    const codeOnPrev = prevLine.match(/^(\d{1,2}(?:\.\d{1,2})+|NP)$/)
    const codeOnCurr = l.match(/^(\d{1,2}(?:\.\d{1,2})+|NP)\s+/)

    const code = codeOnCurr ? codeOnCurr[1] : codeOnPrev ? codeOnPrev[1] : ''

    if (code) {
      const m = code.match(/^(\d{1,2})/)
      if (m) etapaActualNum = parseInt(m[1], 10)
    }

    if (
      PALABRAS_ACCION.test(l) &&
      (l.includes('$') ||
        l.includes('UND') ||
        l.includes('ML') ||
        l.includes('GBL') ||
        l.includes('GLB') ||
        l.includes('M'))
    ) {
      let titulo = l
        .replace(/^(\d{1,2}(?:\.\d{1,2})+|NP)\s+/, '')
        .replace(/\d+[\.,]\d+\$?.*$/, '')
        .trim()

      if (titulo.length > 8) {
        if (!etapasMap[etapaActualNum]) {
          const nombreEtapa =
            nombresCapitulos[etapaActualNum] || `Etapa ${etapaActualNum}`
          etapasMap[etapaActualNum] = {
            orden: etapaActualNum,
            nombre: nombreEtapa,
            modulos: [],
          }
        }

        // Detectar unidad
        let unidad = 'UND'
        if (/\b(ML|M|METROS)\b/i.test(l)) unidad = 'ML'
        else if (/\b(GBL|GLB|GLOBAL)\b/i.test(l)) unidad = 'GLB'
        else if (/\b(M3|M2)\b/i.test(l)) unidad = 'M3'

        // Extraer valor total
        const valoresMatch = l.match(/(\d{1,3}(?:\.\d{3})*(?:,\d+)?)\$/g)
        let valorTotal = 0
        if (valoresMatch && valoresMatch.length > 0) {
          const ultimoVal = valoresMatch[valoresMatch.length - 1]
            .replace(/\$/g, '')
            .replace(/\./g, '')
            .replace(',', '.')
          valorTotal = Number(ultimoVal) || 0
        }

        // Horas estimadas
        const horas = Math.max(
          4,
          Math.min(96, Math.round(valorTotal > 0 ? valorTotal / 280000 : 16))
        )

        const moduloObj: PlanModulo = {
          id: `mod-${id}-${etapaActualNum}-${etapasMap[etapaActualNum].modulos.length + 1}`,
          etapa_id: `etapa-${id}-${etapaActualNum}`,
          codigo: code || `${etapaActualNum}.${etapasMap[etapaActualNum].modulos.length + 1}`,
          nombre: titulo,
          descripcion: null,
          unidad,
          cantidad: 1,
          valor_total: valorTotal,
          horas,
          horas_reales: 0,
          avance: 0,
          estado: 'pendiente',
          orden: etapasMap[etapaActualNum].modulos.length + 1,
          orden_trabajo_id: null,
        }

        etapasMap[etapaActualNum].modulos.push(moduloObj)
      }
    }
  }

  // Fechas estimadas
  const hoyStr = new Date().toISOString().split('T')[0]
  const fechaFin = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split('T')[0]

  // Convertir a array de etapas ordenadas
  const etapasLista = Object.values(etapasMap)
    .sort((a, b) => a.orden - b.orden)
    .map((raw, idx) => {
      const numSecuencial = idx + 1
      const etapaId = `etapa-${id}-${numSecuencial}`
      return {
        id: etapaId,
        proyecto_id: id,
        orden: numSecuencial,
        nombre: raw.nombre,
        fecha_inicio: hoyStr,
        fecha_fin: fechaFin,
        semanas: 2,
        paralela: false,
        estado: 'por_empezar' as const,
        observaciones: null,
        requerimientos: null,
        orden_trabajo_id: null,
        modulos: raw.modulos.map((m, mIdx) => ({
          ...m,
          etapa_id: etapaId,
          orden: mIdx + 1,
        })),
      }
    })

  const presupuestoTotal = etapasLista.reduce(
    (acc, e) => acc + e.modulos.reduce((mAcc, m) => mAcc + m.valor_total, 0),
    0
  )

  return {
    id,
    codigo: `PROY-${Date.now().toString().slice(-6)}`,
    nombre,
    cliente,
    ubicacion,
    fecha_inicio: hoyStr,
    fecha_fin: fechaFin,
    presupuesto: presupuestoTotal > 0 ? presupuestoTotal : 50000000,
    estado: 'propuesta',
    visible_en_matriz: false,
    notas: 'Importado y filtrado automáticamente desde archivo de presupuesto.',
    etapas: etapasLista,
  }
}
